// Plan 14-03 — PowerupTray (MUI-02 power-up cards).
//
// Horizontal scrolling row of power-up cards, one per entry in `loadout`.
// Cards filter against `playerView.available_power_ups[]` (the server-side
// projection from apps/backend/src/match/view/player-view.ts:340) — cards
// in the loadout but NOT currently available render disabled with an
// "unavailable" tooltip.
//
// Pitfall 7 (14-RESEARCH.md): NEVER cache available_power_ups in local
// React state — read on every render from the playerView prop. The
// regression test mounts the tray with a card available, then re-renders
// with that card REMOVED from available_power_ups and asserts the card is
// now disabled. The Pitfall is the cache: a local useState would freeze
// the initial set and miss the server's "you just used it" update.
//
// Onboarding clarity (POWERUP-03 / POWERUP-04): each card is SELF-DOCUMENTING
// — it carries the canon line icon (PowerIcon, not emoji — "no emoji in core
// UI") plus the one-line `match.powerup.desc.*` effect text. A brand-new
// player who never opened the tutorial can read what every power-up does
// straight from the activation tray (mirrors the QuillModePicker title+desc
// stack). Zero new copy — the desc keys already ship in en/sv.
//
// Remaining-use indicator (ARCA-159 / closes ARCA-140): each card carries a
// badge derived from `maxUsesFor(name) - playerView.your_powerups_used[name]`
// so a consumed use is visible. This also SPLITS the prior single disabled
// look into two: EXHAUSTED (used >= max → explicit "USED", dashed destructive
// frame) vs INELIGIBLE-this-phase (uses remain but not in available_power_ups
// → the existing dim, which must NOT read as "USED"). The state is folded into
// each card's aria-label so it is SR-readable, not color-only.
//
// Power-up cell-target contract (deferred to Plan 04/05):
// - Self-targeted (Whisperer's Promise, Lexical Mulligan) — consumer
//   immediately commits setPhaseB({kind:'powerup', name, payload:{}}).
// - Cell-targeted (Scrivener's Quill, Tongue-Tie, Knight's Vow, Castle
//   Keep, Anagram Sigil, The Vanishing) — consumer opens a cell-pick UI;
//   THIS layer's onSelect just delivers the chosen name.
// Plan 14-03 wires only the tray; MatchScreen sets phaseB to a sentinel
// payload `{}` and Plan 04/05 refine the per-power-up follow-up.

import type { JSX } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import type { PlayerView, PowerUpName } from '../../game/PlayerViewSchema'
import { maxUsesFor } from '../../game/powerups/maxUses'
import { PowerIcon, type PowerIconName } from '../components/PowerIcon'

// Backend power-up name → canon line-icon (PowerIcon). Inverse of
// MatchBottomBar's POWERUP_I18N_KEY map — kept here so the activation tray
// renders the same iconography as the always-visible bottom-bar tiles
// (POWERUP-04: replaces the prior emoji glyphs, which violated the
// "no emoji in core UI" rule).
export const POWERUP_ICON: Record<PowerUpName, PowerIconName> = {
  scriveners_quill: 'quill',
  tongue_tie: 'tongue',
  lexical_mulligan: 'mulligan',
  knights_vow: 'vow',
  castle_keep: 'keep',
  anagram_sigil: 'sigil',
  the_vanishing: 'vanish',
  whisperers_promise: 'whisper',
}

export interface PowerupTrayProps {
  readonly playerView: PlayerView
  readonly loadout: readonly PowerUpName[]
  readonly onSelect: (name: PowerUpName) => void
}

export function PowerupTray({
  playerView,
  loadout,
  onSelect,
}: PowerupTrayProps): JSX.Element {
  const intl = useIntl()

  // Pitfall 7 — re-read available_power_ups on every render.
  //
  // WR-02 fix (Phase 14 fix-pass iter 1): the `as unknown` cast was
  // dropped once PlayerViewSchema.ts declared `available_power_ups?:
  // ReadonlyArray<PowerUpName>` as an optional field (matching the
  // backend projection at player-view.ts:340). Default to [] when the
  // field is absent — in-flight matches whose wire payload predates
  // Phase 13 PWR-01 may not carry it, and the server is the authority
  // (DEC-021) so an empty list correctly disables every card.
  const available: readonly PowerUpName[] = playerView.available_power_ups ?? []

  // ARCA-159 — remaining-use indicator source. `your_powerups_used` is the
  // server's per-power-up CONSUMED count (PlayerViewSchema.ts). Default to an
  // empty map for in-flight payloads / test stubs that omit it (used = 0). We
  // derive remaining = maxUsesFor(name) - used to label each card and to split
  // the two disabled looks: EXHAUSTED (used >= max → "USED") vs INELIGIBLE
  // this phase (uses remain but not in available_power_ups → dimmed, NOT
  // "USED"). Like `available`, never cache this — read from props each render.
  const usedMap: Readonly<Record<string, number>> =
    playerView.your_powerups_used ?? {}

  if (loadout.length === 0) {
    return (
      <section
        aria-label={intl.formatMessage({ id: 'match.powerup.tray.aria_label' })}
        data-screen-region="powerup-tray-empty"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 'var(--space-3)',
          color: 'var(--color-text-secondary)',
          fontFamily: 'var(--font-family-body)',
          fontSize: 'var(--font-size-13)',
        }}
      >
        <FormattedMessage id="match.powerup.empty" />
      </section>
    )
  }

  return (
    <section
      aria-label={intl.formatMessage({ id: 'match.powerup.tray.aria_label' })}
      data-screen-region="powerup-tray"
      style={{
        display: 'flex',
        overflowX: 'auto',
        gap: 'var(--space-2)',
        padding: 'var(--space-2)',
      }}
    >
      {loadout.map((name) => {
        const isAvailable = available.includes(name)
        // ARCA-159 — derive the remaining-use state from the server's
        // consumed-count map + the client mirror of MAX_USES_PER_MATCH.
        const max = maxUsesFor(name)
        const used = usedMap[name] ?? 0
        const remaining = Math.max(0, max - used)
        const exhausted = remaining === 0
        // Clickable only when eligible AND not spent. Exhausted is defensive:
        // the server already drops a spent power-up from available_power_ups,
        // but guarding here keeps the two facts from drifting.
        const clickable = isAvailable && !exhausted
        // Three mutually-exclusive display states (exhausted wins):
        //   usable     — uses remain AND eligible this phase
        //   exhausted  — used >= max (spent for the match) → explicit "USED"
        //   ineligible — uses remain but not eligible this phase → dimmed,
        //                must NOT read as "USED" (ARCA-140 bug)
        const state: 'usable' | 'exhausted' | 'ineligible' = exhausted
          ? 'exhausted'
          : isAvailable
            ? 'usable'
            : 'ineligible'
        const nameLabel = intl.formatMessage({
          id: `match.powerup.name.${name}`,
        })
        // Accessible name folds the use state in (ARCA-159 AC: SR-readable,
        // not color-only). One key per state; the existing role-NAME queries
        // still resolve because the localized power-up name leads the string.
        const ariaLabel = intl.formatMessage(
          { id: `match.powerup.aria.${state}` },
          { name: nameLabel, count: remaining },
        )
        const title =
          state === 'usable'
            ? nameLabel
            : state === 'exhausted'
              ? intl.formatMessage({ id: 'match.powerup.exhausted_title' })
              : intl.formatMessage({ id: 'match.error.powerup_unavailable' })
        return (
          <button
            key={name}
            type="button"
            data-powerup-name={name}
            data-powerup-state={state}
            data-powerup-remaining={remaining}
            disabled={!clickable}
            aria-disabled={!clickable}
            aria-label={ariaLabel}
            title={title}
            onClick={() => {
              if (!clickable) return
              onSelect(name)
            }}
            style={{
              position: 'relative',
              flex: '0 0 auto',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              gap: 'var(--space-1)',
              padding: 'var(--space-3)',
              backgroundColor: 'var(--color-surface-mid)',
              color: 'var(--color-text-primary)',
              // EXHAUSTED gets a distinct dashed destructive frame so it never
              // reads the same as the dimmed INELIGIBLE state (non-color cue
              // pairs with the "USED" badge + aria-label).
              border:
                state === 'exhausted'
                  ? '2px dashed var(--color-cta-destructive)'
                  : '2px solid var(--color-surface-stroke)',
              borderRadius: 'var(--radius-md)',
              cursor: clickable ? 'pointer' : 'not-allowed',
              opacity:
                state === 'usable' ? 1 : state === 'exhausted' ? 0.6 : 0.45,
              textAlign: 'left',
              // Token-clean fixed width (5 × --space-6 = 160px) so the effect
              // description has room to wrap to 2-3 lines in the scrolling tray.
              width: 'calc(var(--space-6) * 5)',
            }}
          >
            {/* ARCA-159 — remaining-use badge. Supplementary: the consolidated
                use state already lives in the button aria-label, so hide this
                from SRs (aria-hidden, like the icon) to avoid a double announce.
                One-shots read "1 left" → "USED"; the multi-use trio reads
                "{remaining}/{max}" → "USED". */}
            <span
              aria-hidden="true"
              data-powerup-uses={name}
              style={{
                position: 'absolute',
                top: 'var(--space-1)',
                right: 'var(--space-1)',
                padding: '0 var(--space-1)',
                borderRadius: 'var(--radius-pill)',
                fontFamily: 'var(--font-family-body)',
                fontWeight: 700,
                fontSize: 'var(--font-size-10)',
                lineHeight: 1.6,
                letterSpacing: '0.04em',
                backgroundColor:
                  state === 'exhausted'
                    ? 'var(--color-cta-destructive)'
                    : 'var(--color-surface-deep)',
                color:
                  state === 'exhausted'
                    ? 'var(--color-on-action)'
                    : 'var(--color-text-secondary)',
              }}
            >
              {state === 'exhausted' ? (
                <FormattedMessage id="match.powerup.uses.used" />
              ) : max > 1 ? (
                <FormattedMessage
                  id="match.powerup.uses.fraction"
                  values={{ remaining, max }}
                />
              ) : (
                <FormattedMessage
                  id="match.powerup.uses.left"
                  values={{ remaining }}
                />
              )}
            </span>
            <span
              aria-hidden="true"
              style={{
                display: 'inline-flex',
                color: 'var(--color-cta-primary)',
              }}
            >
              <PowerIcon
                name={POWERUP_ICON[name]}
                size={24}
                title={nameLabel}
              />
            </span>
            <span
              style={{
                fontFamily: 'var(--font-family-display)',
                fontWeight: 700,
                fontSize: 'var(--font-size-14)',
                lineHeight: 1.2,
              }}
            >
              {nameLabel}
            </span>
            {/* POWERUP-03 — self-documenting effect line. Reuses the existing
                match.powerup.desc.* catalog keys (no new copy). */}
            <span
              data-powerup-desc={name}
              style={{
                fontFamily: 'var(--font-family-body)',
                fontWeight: 500,
                fontSize: 'var(--font-size-12)',
                lineHeight: 1.4,
                color: 'var(--color-text-secondary)',
              }}
            >
              <FormattedMessage id={`match.powerup.desc.${name}`} />
            </span>
          </button>
        )
      })}
    </section>
  )
}
